/**
 * Teams configurable / personal tab — pick a process map to pin (PRD §5.5.1)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useAppSelector } from '../../../stores';
import { officeService, TeamsMapSummary } from '../../../services/api/officeService';
import { Spiner } from '../../../components';
import './TeamsEmbed.css';

const TeamsMapTab: React.FC = () => {
    const users = useAppSelector((state) => state.auth);
    const accessToken = users.access_token || '';
    const [maps, setMaps] = useState<TeamsMapSummary[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedId, setSelectedId] = useState<string>('');

    const params = new URLSearchParams(window.location.search);
    const isConfig = params.get('config') === '1';

    const loadMaps = useCallback(async () => {
        if (!accessToken) return;
        setLoading(true);
        setError(null);
        try {
            const list = await officeService.listTeamsMaps(accessToken);
            setMaps(list);
        } catch (err: any) {
            setError(err?.message || 'Failed to load maps');
        } finally {
            setLoading(false);
        }
    }, [accessToken]);

    useEffect(() => {
        void loadMaps();
    }, [loadMaps]);

    const notifyTeamsConfig = (map: TeamsMapSummary) => {
        try {
            const microsoftTeams = (window as any).microsoftTeams || (window as any).microsoftTeams;
            // Teams JS v2
            const teams = (window as any).microsoftTeams;
            if (teams?.pages?.config) {
                teams.pages.config.setValidityState(true);
                teams.pages.config.registerOnSaveHandler((saveEvent: any) => {
                    teams.pages.config.setConfig({
                        entityId: map.id,
                        contentUrl: map.embedUrl,
                        websiteUrl: map.viewUrl,
                        suggestedDisplayName: map.title,
                    });
                    saveEvent.notifySuccess();
                });
            } else if ((window as any).microsoftTeams?.settings) {
                const settings = (window as any).microsoftTeams.settings;
                settings.setValidityState(true);
                settings.registerOnSaveHandler((saveEvent: any) => {
                    settings.setSettings({
                        entityId: map.id,
                        contentUrl: map.embedUrl,
                        websiteUrl: map.viewUrl,
                        suggestedDisplayName: map.title,
                    });
                    saveEvent.notifySuccess();
                });
            }
        } catch {
            /* not in Teams host */
        }
    };

    const handleSelect = (map: TeamsMapSummary) => {
        setSelectedId(map.id);
        if (isConfig) {
            notifyTeamsConfig(map);
        } else if (map.embedUrl) {
            window.location.href = map.embedUrl;
        }
    };

    if (!accessToken) {
        return (
            <div className="teams-embed">
                <h1>Famla</h1>
                <p>Sign in to Famla in this browser tab, then reopen the Teams tab configuration.</p>
                <a className="teams-embed__cta" href="/auth/login" target="_blank" rel="noreferrer">
                    Sign in to Famla
                </a>
            </div>
        );
    }

    return (
        <div className="teams-embed">
            <header className="teams-embed__header">
                <h1>{isConfig ? 'Pin a process map' : 'Your process maps'}</h1>
                <p>
                    {isConfig
                        ? 'Select a published map to show read-only in this channel. Editing always opens Famla.'
                        : 'Open a map in read-only viewer, or edit it in Famla.'}
                </p>
            </header>

            {loading && <Spiner className="d-flex justify-content-center spinner-border-sm" />}
            {error && <p className="teams-embed__error">{error}</p>}

            <ul className="teams-embed__list">
                {maps.map((map) => (
                    <li key={map.id}>
                        <button
                            type="button"
                            className={`teams-embed__map-btn ${selectedId === map.id ? 'is-selected' : ''}`}
                            onClick={() => handleSelect(map)}
                        >
                            <span className="teams-embed__map-title">{map.title}</span>
                            <span className="teams-embed__map-meta">
                                {map.ownerName || '—'} · {map.stepCount} steps
                            </span>
                        </button>
                    </li>
                ))}
            </ul>

            {!loading && maps.length === 0 && (
                <p className="teams-embed__empty">No process maps found for your company yet.</p>
            )}
        </div>
    );
};

export default TeamsMapTab;
